import { Box, ClickAwayListener, Tooltip } from "@mui/material";
import { Minus, Plus } from "@pmag/icons";
import { IconButton } from "@pmag/ui-components";
import { type GQLComponentSubGroupGenericFragment, type GQLHotspotFragment } from "@src/graphql.generated";
import { SmallListItem } from "@src/pages/subGroupDetail/subgroupDetailsListSection/smallListItem/SmallListItem";
import { type SPFArticleWithPriceInfo } from "@src/utils/events";
import { type FunctionComponent, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { type ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";

import {
    BoxActions,
    BoxTooltip,
    BoxZoomContainer,
    ExplosionGraphicHotspot,
    ExplosionGraphicHotspotGhost,
    ExplosionGraphicHotspots,
    ExplosionGraphicImage,
    ModalStyled,
} from "./ExplosionGraphicModal.styles";
import { getSparePartByHotspot } from "./ExplosionGraphicModal.utils";

export const dataTestId = {
    modal: "sparePartsFinder.subgroupPage.explosionGraphicModal.modal",
    hotspots: "sparePartsFinder.subgroupPage.explosionGraphicModal.hotspots",
    hotspot: "sparePartsFinder.subgroupPage.explosionGraphicModal.hotspot",
    transformComponent: "sparePartsFinder.subgroupPage.explosionGraphicModal.transformComponent",
    image: "sparePartsFinder.subgroupPage.explosionGraphicModal.image",
    zoomInButton: "sparePartsFinder.subgroupPage.explosionGraphicModal.button.zoomIn",
    zoomOutButton: "sparePartsFinder.subgroupPage.explosionGraphicModal.button.zoomOut",
    selectSparePartButton: "sparePartsFinder.subgroupPage.explosionGraphicModal.button.selectSparePart",
    availabilityStatus: "sparePartsFinder.subgroupPage.explosionGraphicModal.availabilityStatus",
};

export interface ExplosionGraphicModalProps {
    brand: string;
    clientWishlist: string[];
    componentGroupItem: GQLComponentSubGroupGenericFragment;
    hotspots: GQLHotspotFragment[];
    modelId: string;
    onClickCancel: () => void;
    open: boolean;
    spareParts: SPFArticleWithPriceInfo[];
}

interface HTMLImageElementWithLoadEvent extends HTMLImageElement {
    onerror: () => void;
}

export const ExplosionGraphicModal: FunctionComponent<ExplosionGraphicModalProps> = ({
    brand,
    modelId,
    open,
    componentGroupItem,
    hotspots,
    spareParts,
    onClickCancel,
    clientWishlist,
}) => {
    const intl = useIntl();
    const zoomWrapper = useRef<ReactZoomPanPinchRef>(null);
    const boxZoomContainer = useRef<HTMLElement>(null);
    const boxZoomContent = useRef<HTMLElement>(null);
    const hotspotsWithSparePart = hotspots.map((hotspot) => {
        return {
            ...hotspot,
            ...{
                sparePart: getSparePartByHotspot(spareParts, hotspot),
            },
        };
    });
    const hotspotsWithSparePartDefined = hotspotsWithSparePart.filter((hotspot) => hotspot.sparePart);

    const [imageLoaded, setImageLoaded] = useState<boolean>(false);
    const image = useRef<HTMLImageElementWithLoadEvent>(null);

    const [tooltipsOpen, setTooltipsOpen] = useState(hotspotsWithSparePartDefined.map(() => false));

    const checkImage = () => {
        if (boxZoomContainer.current && boxZoomContent.current && zoomWrapper.current) {
            const boxZoomContainerBoundingClientRect = boxZoomContainer.current.getBoundingClientRect();
            const boxZoomContentBoundingClientRect = boxZoomContent.current.getBoundingClientRect();
            const { width: widthBoxZoomContainer } = boxZoomContainerBoundingClientRect;
            const { width: widthBoxZoomContent } = boxZoomContentBoundingClientRect;
            const { height: heightBoxZoomContainer } = boxZoomContainerBoundingClientRect;
            const { height: heightBoxZoomContent } = boxZoomContentBoundingClientRect;
            const widthScale = widthBoxZoomContainer / widthBoxZoomContent;
            const heightScale = heightBoxZoomContainer / heightBoxZoomContent;
            const scale = widthScale < heightScale ? widthScale : heightScale;
            const xOffset = widthScale < heightScale ? 0 : (widthBoxZoomContainer - widthBoxZoomContent * scale) / 2;
            const yOffset = widthScale < heightScale ? (heightBoxZoomContainer - heightBoxZoomContent * scale) / 2 : 0;
            zoomWrapper.current.setTransform(xOffset, yOffset, scale, 0);
        }
        setImageLoaded(true);
    };

    const replaceImage = () => {
        if (!imageLoaded) {
            image.current?.removeEventListener("load", checkImage);
            image.current?.removeEventListener("error", replaceImage);
            image.current?.setAttribute("src", componentGroupItem.explosionAssetMediumResolution ?? "");
            setImageLoaded(true);
        }
    };

    const openTooltip = (index: number) => {
        const updated = hotspotsWithSparePartDefined.map((_, i) => i === index);
        setTooltipsOpen(updated);
    };

    const closeTooltips = () => {
        const allClosed = hotspotsWithSparePartDefined.map(() => false);
        setTooltipsOpen(allClosed);
    };

    const assetPath = componentGroupItem.explosionAssetHighResolution ?? componentGroupItem.explosionAssetMediumResolution;

    return (
        <ModalStyled
            severity="info"
            severityText={intl.formatMessage({
                id: "sparePartsFinder.subgroupPage.explosionGraphicModal.severityText",
                defaultMessage: "Illustration",
            })}
            showSeverityBorder={false}
            variant="background"
            open={open}
            disablePadding={true}
            onClose={() => {
                setImageLoaded(false);
                onClickCancel();
            }}
            data-testid={dataTestId.modal}
        >
            <BoxZoomContainer ref={boxZoomContainer}>
                <TransformWrapper minScale={0.5} maxScale={2} doubleClick={{ step: 0.4 }} ref={zoomWrapper}>
                    {({ zoomIn, zoomOut }) => (
                        <>
                            <BoxActions>
                                <IconButton variant="ghost" onClick={() => zoomIn()} data-testid={dataTestId.zoomInButton}>
                                    <Plus />
                                </IconButton>

                                <IconButton variant="ghost" onClick={() => zoomOut()} data-testid={dataTestId.zoomOutButton}>
                                    <Minus />
                                </IconButton>
                            </BoxActions>

                            <TransformComponent contentProps={{ role: "group" }}>
                                <Box ref={boxZoomContent}>
                                    <ExplosionGraphicHotspots data-testid={dataTestId.hotspots}>
                                        {hotspotsWithSparePartDefined.map((hotspot, index) => (
                                            <ExplosionGraphicHotspot
                                                left={`${hotspot.startX * 100}%`}
                                                top={`${(1 - hotspot.endY) * 100}%`}
                                                width={`${(hotspot.endX - hotspot.startX) * 100}%`}
                                                height={`${(hotspot.endY - hotspot.startY) * 100}%`}
                                                key={hotspot.id}
                                                data-testid={dataTestId.hotspot}
                                            >
                                                {hotspot.sparePart && (
                                                    <Tooltip
                                                        open={tooltipsOpen[index] ?? false}
                                                        onOpen={() => openTooltip(index)}
                                                        arrow={true}
                                                        title={
                                                            <ClickAwayListener onClickAway={closeTooltips}>
                                                                <BoxTooltip onClick={(e) => e.stopPropagation()}>
                                                                    <SmallListItem
                                                                        brand={brand}
                                                                        modelId={modelId}
                                                                        sparePart={hotspot.sparePart}
                                                                        showBorderBottom={false}
                                                                        onClose={closeTooltips}
                                                                        clientWishlist={clientWishlist}
                                                                    />
                                                                </BoxTooltip>
                                                            </ClickAwayListener>
                                                        }
                                                    >
                                                        <ExplosionGraphicHotspotGhost />
                                                    </Tooltip>
                                                )}
                                            </ExplosionGraphicHotspot>
                                        ))}
                                    </ExplosionGraphicHotspots>

                                    {assetPath && (
                                        <ExplosionGraphicImage
                                            src={assetPath}
                                            onLoad={checkImage}
                                            onError={replaceImage}
                                            data-testid={dataTestId.image}
                                            ref={image}
                                        />
                                    )}
                                </Box>
                            </TransformComponent>
                        </>
                    )}
                </TransformWrapper>
            </BoxZoomContainer>
        </ModalStyled>
    );
};
